import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { getActiveTariffConfig, DEFAULT_CONFIG, calcularTarifa, estimarTiempo } from '../utils/tarifaCalculator';
import { useExchangeRate } from '../utils/useExchangeRate';
import { useAuth } from '../../USER/Auth/AuthContext';
import { VEHICLE_TYPES } from '../../../constants';
import './PriceCalculator.css';

const VEHICLE_OPTIONS = [
    { id: VEHICLE_TYPES.MOTO || 'moto', label: 'Moto', mult: '1.0x' },
    { id: VEHICLE_TYPES.CARRO || 'carro', label: 'Carro', mult: '1.5x' },
];

const SERVICE_OPTIONS = [
    { id: 'ride', label: 'Viaje' },
    { id: 'delivery', label: 'Delivery' },
    { id: 'errand', label: 'Encomienda' },
];

const DISTANCE_PRESETS = [2, 5, 10, 15];

const normalizeServiceType = (val) => {
    if (!val) return 'ride';
    if (val === 'mototaxi') return 'ride';
    if (val === 'encomienda') return 'errand';
    return val;
};

const normalizeVehicleType = (val) => {
    if (!val) return VEHICLE_TYPES.MOTO || 'moto';
    if (val === 'auto') return VEHICLE_TYPES.CARRO || 'carro';
    return val;
};

const PriceCalculator = ({
    distanciaInicial = 0,
    pesoInicial = 0,
    tiempoInicial = 0,
    vehicleTypeInicial = 'moto',
    tipoServicioInicial,
    serviceTypeInicial,
    onPriceCalculated,
    onTarifaCalculada,
    onConfirmarTarifa
}) => {
    const initialDist = parseFloat(distanciaInicial) > 0 ? parseFloat(distanciaInicial) : 2.5;
    const initialTime = parseFloat(tiempoInicial) > 0 ? parseFloat(tiempoInicial) : estimarTiempo(initialDist);

    const [distancia, setDistancia] = useState(initialDist);
    const [peso, setPeso] = useState(pesoInicial || 0);
    const [tiempoEstimado, setTiempoEstimado] = useState(initialTime);
    const [vehicleType, setVehicleType] = useState(normalizeVehicleType(vehicleTypeInicial));
    const [tipoServicio, setTipoServicio] = useState(
        normalizeServiceType(tipoServicioInicial || serviceTypeInicial)
    );
    const [config, setConfig] = useState(DEFAULT_CONFIG);
    const [loading, setLoading] = useState(true);

    const { rate } = useExchangeRate();
    const { userRole, isSuperAdmin, isAdmin } = useAuth();
    const showDriverSplit = isSuperAdmin || isAdmin || userRole === 'driver';

    useEffect(() => {
        let isMounted = true;
        getActiveTariffConfig()
            .then((res) => {
                if (isMounted && res) setConfig(res);
            })
            .catch(() => {
                if (isMounted) setConfig(DEFAULT_CONFIG);
            })
            .finally(() => {
                if (isMounted) setLoading(false);
            });
        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        if (parseFloat(distanciaInicial) > 0) {
            setDistancia(parseFloat(distanciaInicial));
            setTiempoEstimado(estimarTiempo(parseFloat(distanciaInicial)));
        }
    }, [distanciaInicial]);

    useEffect(() => {
        if (vehicleTypeInicial) {
            setVehicleType(normalizeVehicleType(vehicleTypeInicial));
        }
    }, [vehicleTypeInicial]);

    useEffect(() => {
        const nextService = tipoServicioInicial || serviceTypeInicial;
        if (nextService) {
            setTipoServicio(normalizeServiceType(nextService));
        }
    }, [tipoServicioInicial, serviceTypeInicial]);

    const effectiveWeight = (tipoServicio === 'delivery' || tipoServicio === 'errand')
        ? (parseFloat(peso) || 0)
        : 0;

    const resultado = useMemo(() => {
        const distNum = Math.max(0.5, parseFloat(distancia) || 0.5);
        const timeNum = Math.max(1, parseFloat(tiempoEstimado) || estimarTiempo(distNum));
        const calc = calcularTarifa(distNum, effectiveWeight, timeNum, config, vehicleType);
        return {
            ...calc,
            totalNum: parseFloat(calc.total) || 0,
            commissionNum: parseFloat(calc.commission) || 0,
            driverPayoutNum: parseFloat(calc.driverPayout) || 0,
            costoDistancia: calc.breakdown?.distancia || 0,
            costoTiempo: calc.breakdown?.tiempo || 0,
            tarifaBase: calc.breakdown?.base || (config.base_price || 2.0),
        };
    }, [distancia, tiempoEstimado, effectiveWeight, config, vehicleType]);

    useEffect(() => {
        if (resultado) {
            if (onPriceCalculated) onPriceCalculated(resultado.totalNum, resultado);
            if (onTarifaCalculada) onTarifaCalculada(resultado);
        }
    }, [resultado, onPriceCalculated, onTarifaCalculada]);

    const handleDistancePreset = (km) => {
        setDistancia(km);
        setTiempoEstimado(estimarTiempo(km));
    };

    if (loading) {
        return (
            <div className="price-calc-loading">
                <div className="spinner-border spinner-border-sm text-info me-2" role="status" />
                <span>Cargando tarifas...</span>
            </div>
        );
    }

    const recorridoYMinutos = Number((resultado.costoDistancia + resultado.costoTiempo).toFixed(2));
    const vesTotal = rate && resultado.totalNum ? (resultado.totalNum * rate).toFixed(2) : null;

    return (
        <div className="price-calc-shell">
            {/* 1. Hero Total Result */}
            <div className="price-calc-hero">
                <div className="price-calc-hero-top">
                    <span className="price-calc-hero-label">Total Estimado</span>
                    <span className="price-calc-hero-badges">
                        <span className="price-calc-pill">{Number(distancia || 0).toFixed(1)} km</span>
                        <span className="price-calc-pill">~{Math.round(tiempoEstimado || 0)} min</span>
                        {resultado.surgeActive && (
                            <span className="price-calc-pill price-calc-pill-night">Alta demanda</span>
                        )}
                    </span>
                </div>
                <div className="price-calc-hero-amount d-flex align-items-baseline gap-2">
                    <span style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--bry-text-main, #0F172A)' }}>
                        ${resultado.totalNum.toFixed(2)}
                    </span>
                    <span style={{ fontSize: '0.95rem', fontWeight: 800, color: '#0EABB0' }}>
                        USD
                    </span>
                    {vesTotal && (
                        <span className="ms-2 px-2 py-0.5 rounded-pill" style={{ fontSize: '0.88rem', fontWeight: 700, backgroundColor: 'rgba(14, 171, 176, 0.12)', color: '#088D91' }}>
                            ~Bs. {vesTotal}
                        </span>
                    )}
                </div>
                {rate && (
                    <div className="price-calc-rate-note">
                        Tasa de cambio: Bs. {Number(rate).toFixed(2)} / USD
                    </div>
                )}
            </div>

            {/* 2. Selectores rápidos de Vehículo y Servicio */}
            <div className="price-calc-section">
                <label className="price-calc-label">Vehículo</label>
                <div className="price-calc-segmented">
                    {VEHICLE_OPTIONS.map((v) => (
                        <button
                            key={v.id}
                            type="button"
                            className={`price-calc-seg-btn ${vehicleType === v.id ? 'active' : ''}`}
                            onClick={() => setVehicleType(v.id)}
                        >
                            <span>{v.label}</span>
                            <small>{v.mult}</small>
                        </button>
                    ))}
                </div>
            </div>

            <div className="price-calc-section">
                <label className="price-calc-label">Tipo de servicio</label>
                <div className="price-calc-segmented">
                    {SERVICE_OPTIONS.map((s) => (
                        <button
                            key={s.id}
                            type="button"
                            className={`price-calc-seg-btn ${tipoServicio === s.id ? 'active' : ''}`}
                            onClick={() => setTipoServicio(s.id)}
                        >
                            <span>{s.label}</span>
                        </button>
                    ))}
                </div>
            </div>

            {/* 3. Distancia y Tiempo con Presets rápidos */}
            <div className="price-calc-section">
                <div className="d-flex justify-content-between align-items-center mb-1">
                    <label className="price-calc-label mb-0">Distancia y tiempo</label>
                    <div className="price-calc-presets">
                        {DISTANCE_PRESETS.map((km) => (
                            <button
                                key={km}
                                type="button"
                                className={`price-calc-chip ${Number(distancia) === km ? 'active' : ''}`}
                                onClick={() => handleDistancePreset(km)}
                            >
                                {km} km
                            </button>
                        ))}
                    </div>
                </div>

                <div className="price-calc-inputs-grid mt-1">
                    <div className="price-calc-field">
                        <span className="price-calc-field-caption">Distancia manual (km)</span>
                        <input
                            type="number"
                            step="0.5"
                            min="0.5"
                            value={distancia}
                            onChange={(e) => {
                                const val = e.target.value;
                                setDistancia(val);
                                if (parseFloat(val) > 0) {
                                    setTiempoEstimado(estimarTiempo(parseFloat(val)));
                                }
                            }}
                            className="price-calc-input"
                        />
                    </div>
                    <div className="price-calc-field">
                        <span className="price-calc-field-caption">Tiempo est. (min)</span>
                        <input
                            type="number"
                            step="1"
                            min="1"
                            value={tiempoEstimado}
                            onChange={(e) => setTiempoEstimado(e.target.value)}
                            className="price-calc-input"
                        />
                    </div>
                    {(tipoServicio === 'delivery' || tipoServicio === 'errand') && (
                        <div className="price-calc-field price-calc-field-full">
                            <span className="price-calc-field-caption">Peso del paquete (kg, primeros 5 kg sin recargo)</span>
                            <input
                                type="number"
                                step="0.5"
                                min="0"
                                value={peso}
                                onChange={(e) => setPeso(e.target.value)}
                                className="price-calc-input"
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* 4. Desglose resumido y legible */}
            <div className="price-calc-breakdown">
                <div className="price-calc-row">
                    <span>Tarifa base ({VEHICLE_OPTIONS.find(v => v.id === vehicleType)?.label || 'Moto'})</span>
                    <strong>${resultado.tarifaBase.toFixed(2)}</strong>
                </div>
                <div className="price-calc-row">
                    <span>Recorrido ({Number(distancia || 0).toFixed(1)} km) y tiempo ({Math.round(tiempoEstimado || 0)} min)</span>
                    <strong>${recorridoYMinutos.toFixed(2)}</strong>
                </div>
                {resultado.breakdown?.factorPeso > 1 && (
                    <div className="price-calc-row">
                        <span>Recargo por peso</span>
                        <strong>x{resultado.breakdown.factorPeso}</strong>
                    </div>
                )}
                {resultado.totalNum === (config.min_price || 2.5) && (
                    <div className="price-calc-min-note">
                        Se aplica tarifa mínima de ${(config.min_price || 2.5).toFixed(2)} USD
                    </div>
                )}
                {showDriverSplit && (
                    <div className="price-calc-driver-net">
                        <span>Ganancia neta conductor estimada</span>
                        <strong>${resultado.driverPayoutNum.toFixed(2)} USD</strong>
                    </div>
                )}
            </div>

            {/* 5. Botón Sticky de Confirmación (Siempre Visible) */}
            {onConfirmarTarifa && (
                <div className="price-calc-sticky-footer">
                    <button
                        type="button"
                        className="price-calc-confirm-btn"
                        onClick={() => onConfirmarTarifa(resultado)}
                    >
                        <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                        Confirmar Tarifa (${resultado.totalNum.toFixed(2)} USD)
                    </button>
                </div>
            )}
        </div>
    );
};

export default PriceCalculator;
